import { useEffect, useRef } from "react";

import { useSelector } from "react-redux";

import { useGetCartQuery, useRemoveLineItemFromCartMutation } from "@store/services/shopify";
import { useGetUserDocumentsQuery } from "@store/services/userDocuments";
import { useGetUserDetailsQuery } from "@store/services/user";
import { type RootState } from "@store/rootReducer";

import { findStaleLineIds } from "./useCartSanityCheck.utilities";

/**
 * Silently drops any line whose referenced document has been deleted since it
 * was added — including bundle lines where a single contained document
 * is gone. Reads the raw Shopify cart (whose lines carry the
 * externalDocumentIds attribute) and the user's documents as the source of
 * truth for existence; both share their cache with the rest of the app, so
 * this adds no extra network requests.
 */
export const useCartSanityCheck = () => {
  const shopifyCartId = useSelector((state: RootState) => state.cart.shopifyCartId);

  const { data: userDetailsData } = useGetUserDetailsQuery();
  const isAuthenticated = userDetailsData?.authenticated === true;

  const { data: cart } = useGetCartQuery(
    { cartId: shopifyCartId ?? "" },
    { skip: !isAuthenticated || shopifyCartId == null },
  );

  const { data: documents } = useGetUserDocumentsQuery(undefined, { skip: !isAuthenticated });

  const [removeLineItem] = useRemoveLineItemFromCartMutation();

  // Each removal writes the whole returned cart back into the getCart cache, so
  // overlapping removals race: a slower response reflecting a pre-removal state
  // could overwrite the cache and resurrect an already-removed line. Guard so a
  // fresh sweep can't start while one is still draining — the effect re-runs on
  // every cache write, which would otherwise re-enter mid-removal.
  const isRemovingRef = useRef(false);

  useEffect(() => {
    if (isRemovingRef.current) {
      return;
    }

    // Wait until both the cart and the document list have settled — a partial
    // view would risk removing lines whose documents simply haven't loaded yet.
    if (cart == null || documents == null || shopifyCartId == null) {
      return;
    }

    const existingDocumentIds = new Set(documents.map((document) => document.id));
    const staleLineIds = findStaleLineIds(cart.lines, existingDocumentIds);

    if (staleLineIds.length === 0) {
      return;
    }

    isRemovingRef.current = true;

    // Remove sequentially so the cache is written in order, never concurrently.
    void (async () => {
      try {
        for (const lineItemId of staleLineIds) {
          await removeLineItem({ cartId: shopifyCartId, lineItemId });
        }
      } finally {
        isRemovingRef.current = false;
      }
    })();
  }, [cart, documents, shopifyCartId, removeLineItem]);
};
